/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { needleWarningMessage } from '../utils';

type UseInlineFieldLabelOptions = {
  /** Component name used in the missing-label console warning. */
  componentName: string;
  /** The label rendered next to the control, inside its wrapping `<label>`. */
  label: React.ReactNode;
  // TODO v5: only needed while naming a selection control is split across
  // `label` and `ariaLabel`. Whenever the label alone names the control, as it
  // does on Combobox, the warning below has nothing left to check.
  /** The aria label naming the control when the label is not a string. */
  ariaLabel: string | undefined;
  /** Dims the label and the control it belongs to. */
  isDisabled?: boolean;
  /** Lets the field take the full available width of its container. */
  isFluid?: boolean;
  /**
   * Truncates an overflowing string label to one line with an ellipsis (the
   * `title` set on the span keeps the full text reachable). Radio behavior
   * today; a candidate to unify across all selection controls in v5.
   */
  isTruncated?: boolean;
};

type UseInlineFieldLabelResult = {
  /** The label, rendered next to the control. */
  labelContent: React.ReactNode;
  /**
   * Classes for the `<label>` that wraps the control and the label, which the
   * adopting component renders. Owned here so that the shared classes stay an
   * implementation detail of this module.
   */
  fieldClassName: string;
};

/**
 * The inline label of a selection control (Checkbox, Radio, Switch): the
 * control and its label text sit side by side inside one wrapping `<label>`.
 * A string label is wrapped in a titled span so a truncated label stays
 * readable through its tooltip; a custom node is rendered as given, which is
 * also why it needs `ariaLabel` to name the control. Warns when neither a
 * label nor an aria label is provided.
 *
 * Returns the classes of the wrapping label along with its content, so that
 * an adopting component never names a shared class itself. Typography and
 * color sit on that wrapper rather than on the span here, so that a custom
 * node inherits them too.
 *
 * TODO v5: wrap a custom node as well, once the DOM of a custom label can
 * change. This would own all label styling, and the difference between the
 * full-strength inline label and the weaker label above a control could then
 * become a prop here instead of living in two stylesheet classes.
 */
export const useInlineFieldLabel = ({
  componentName,
  label,
  ariaLabel,
  isDisabled = false,
  isFluid = false,
  isTruncated = false,
}: UseInlineFieldLabelOptions): UseInlineFieldLabelResult => {
  useEffect(() => {
    if (Boolean(label) === false && Boolean(ariaLabel) === false) {
      needleWarningMessage(
        `${componentName} needs a valid label or ariaLabel: https://dequeuniversity.com/rules/axe/4.2/label?application=axeAPI`,
      );
    }
  }, [ariaLabel, componentName, label]);

  const labelContent = useMemo(() => {
    if (Boolean(label) === false) {
      return null;
    }

    if (typeof label === 'string') {
      return (
        <span
          title={label}
          className={classNames('ndl-form-inline-label-text', {
            'ndl-disabled': isDisabled,
            'ndl-truncated': isTruncated,
          })}
        >
          {label}
        </span>
      );
    }

    return label;
  }, [label, isDisabled, isTruncated]);

  return {
    fieldClassName: classNames('ndl-form-inline-field', {
      'ndl-disabled': isDisabled,
      'ndl-fluid': isFluid,
    }),
    labelContent,
  };
};
